import React, { type CSSProperties, type ReactNode } from 'react';
import { CountdownRing } from './CountdownRing';
import { TOTAL_SLIDES } from '../data/deckContent';

/**
 * Two chapters of the same Solventum system. `strategy` is the established
 * deep-green field with the bright green accent; `tactics` steps up to a
 * clearly lighter mid-green field with a mint accent from the same brand
 * family, so the execution half is unmistakably a different act.
 */
const CHAPTERS = {
  strategy: {
    bg: '#05110c',
    accent: '#4ade80',
    accentRgb: '74,222,128'
  },
  tactics: {
    bg: '#0f3527',
    accent: '#7ff0cf',
    accentRgb: '127,240,207'
  }
} as const;
export type SlideChapter = keyof typeof CHAPTERS;
type SlideFrameProps = {
  eyebrow: string;
  index: number;
  /** Hide the corner countdown on slides that already show the hero ring. */
  showMiniCountdown?: boolean;
  /** Which half of the deck this slide belongs to. */
  chapter?: SlideChapter;
  children: ReactNode;
};

/**
 * Shared 16:9 full-bleed slide chrome: deep-green field, dot grid, soft accent
 * bloom, thin arcs, and the persistent eyebrow / brand / footer rails.
 */
export function SlideFrame({
  eyebrow,
  index,
  showMiniCountdown = true,
  chapter = 'strategy',
  children
}: SlideFrameProps) {
  const {
    bg,
    accent,
    accentRgb
  } = CHAPTERS[chapter];
  return <section className="relative h-full w-full overflow-hidden" style={{
    backgroundColor: bg,
    // Scoped tokens: children reading var(--sv-green) / var(--sv-bg)
    // follow the chapter they are rendered in.
    '--sv-green': accent,
    '--sv-bg': bg
  } as CSSProperties} aria-label={eyebrow} data-id="element-29" data-mp="2s5ca130fjhnn">
      {/* dot grid */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.35]" style={{
      backgroundImage: 'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)',
      backgroundSize: '26px 26px'
    }} data-id="element-30" data-mp="29eutrxwsaej6" />
      {/* accent bloom */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      background: `radial-gradient(75% 60% at 78% 18%, rgba(${accentRgb},0.16) 0%, rgba(5,17,12,0) 62%)`
    }} data-id="element-31" data-mp="2v4b30kglexqe" />
      {/* thin arcs */}
      <svg aria-hidden="true" viewBox="0 0 1600 900" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" data-id="element-32" data-mp="3045vgsg0nvdk">
        <circle cx="1180" cy="-140" r="720" fill="none" stroke={`rgba(${accentRgb},0.20)`} strokeWidth="2" data-id="element-33" data-mp="27g1ps6xg3iav" />
        <circle cx="140" cy="1080" r="620" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2" data-id="element-34" data-mp="2m70s507otam0" />
      </svg>

      <div className="relative flex h-full w-full flex-col px-[6%] py-[4.5%]" data-id="element-35" data-mp="1tn25zsqmr1xi">
        <header className="flex shrink-0 items-center justify-between" data-id="element-36" data-mp="1ooqznrzjn9k1">
          <p className="text-[clamp(9px,0.85vw,15px)] font-medium uppercase tracking-[0.32em] text-white/45" data-id="element-37" data-mp="46zmchqfchg0">
            {eyebrow}
            <span className="text-[color:var(--sv-green)]" data-id="element-38" data-mp="8yzrcolcq0jg">
              {' '}
              · {index} of {TOTAL_SLIDES}
            </span>
          </p>
          <div className="flex items-center gap-[1.1vw]" data-id="element-39" data-mp="3vdj82rxstrt3">
            <p className="text-[clamp(9px,0.85vw,15px)] font-semibold uppercase tracking-[0.32em] text-white/70" data-id="element-40" data-mp="3ve2g9gxnd1tx">
              Solventum Dental
            </p>
            {showMiniCountdown ? <CountdownRing size="sm" data-id="element-41" data-mp="rvrc72gjknpm" /> : null}
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col justify-center" data-id="element-42" data-mp="dy2ntkn9myft">
          {children}
        </div>

        <footer className="shrink-0 text-[clamp(8px,0.7vw,12px)] tracking-wide text-white/25" data-id="element-43" data-mp="hxv12anltsri">
          For dental professionals. Internal strategic concept · © Solventum 2026
        </footer>
      </div>
    </section>;
}